import { useTranslation } from "react-i18next";
import {
  askForProfessionalRecord,
  needsProfessionalRecord,
} from "../../components/room/ProfessionalRecordHost";
import { useHistory } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { displayToast } from "../../components/app/AppToast";
import {
  gaEventRoomJoin,
  type RoomDiscoveryChannel,
} from "../../tools/analytics/domains/invite.analytics";
import { gaEventCompleteOnboarding } from "../../tools/analytics/domains/auth.analytics";
import { getUserStartingPage } from "../../domains/user/utils/getUserStartingPage";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { getRoomInvitePathname } from "../../domains/room/utils/getRoomPathname";
import { apiClient, useApiQuery, useApiRevalidate } from "../../services/api";
import { openRoomWelcome } from "../../components/room/RoomWelcomeHost";
import { OauthRoomsConfig } from "../../features/oauth";
import {
  clearRoomInviteCodeData,
  roomInviteCodeStore,
  TWO_WEEKS_IN_MS,
} from "../../domains/auth/stores/roomInviteCodeStore";
import { resetSignupFirstVisitFlags } from "../../domains/user/stores/firstVisitFlagsStore";
import { consumeOAuthSignupCompleted } from "../../domains/auth/stores/oauthSignupCompletionStore";

type OAuthFlowState =
  | { status: "pending" }
  | { status: "ready" }
  | { status: "joining" }
  | { status: "error" };

export function useRoomPreviewOAuthCallback(roomId: string) {
  const { t } = useTranslation();
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const searchParams = new URLSearchParams(location.search);

  const utmSource = searchParams.get("utm_source");
  const utmMedium = searchParams.get("utm_medium");
  const utmContent = searchParams.get("utm_content");

  const storedRoomInviteCodeData = roomInviteCodeStore.get();
  const playlistId =
    storedRoomInviteCodeData === null
      ? null
      : storedRoomInviteCodeData.roomPlaylistId;

  const roomQuery = useApiQuery("/webapp/public/rooms/:roomId/stats", {
    param: { roomId },
  });
  const room = roomQuery.state === "success" ? roomQuery.body : null;
  const isLoadingRoomData = roomQuery.state === "pending";

  const revalidate = useApiRevalidate();

  const history = useHistory();

  const [oauthFlow, setOauthFlow] = useState<OAuthFlowState>({
    status: "pending",
  });

  const errorRedirect = useCallback(async () => {
    if (currentUser === null) {
      history.replace("/");
      return;
    }
    history.replace(await getUserStartingPage(currentUser));
  }, [currentUser, history]);

  const onJoinRoom = async () => {
    const roomStats = room;
    if (roomStats === null || currentUser === null) {
      await errorRedirect();
      return;
    }

    // Fresh room join — reset first-visit flags so the download-app modal and
    // profile-picture prompt fire again.
    resetSignupFirstVisitFlags();

    let roomInviteCode: string | null = null;
    let storedDiscoveryChannel: RoomDiscoveryChannel | null = null;
    if (storedRoomInviteCodeData !== null) {
      if (Date.now() > storedRoomInviteCodeData.timestamp + TWO_WEEKS_IN_MS) {
        console.warn("Expired roomInviteCodeData in localStorage, clearing");
        clearRoomInviteCodeData();
        displayToast(
          t("Your invite code has expired. Please try again."),
          "error"
        );
        await errorRedirect();
        return;
      }
      roomInviteCode = storedRoomInviteCodeData.roomInviteCode;
      storedDiscoveryChannel = storedRoomInviteCodeData.roomDiscoveryChannel;
    }

    // A room that asks for a professional record admits nobody without one.
    // The invite page and the onboarding ask before the partner sign-in; this
    // is the safety net for a reader who reaches the callback without it.
    if (needsProfessionalRecord(roomStats, currentUser)) {
      const outcome = await askForProfessionalRecord();
      if (outcome === "cancelled") {
        clearRoomInviteCodeData();
        await errorRedirect();
        return;
      }
    }

    try {
      if (roomInviteCode !== null) {
        await apiClient.post("/webapp/private/rooms/join/by-invite/:code", {
          param: { code: roomInviteCode },
          json: {
            discoveryChannel:
              storedDiscoveryChannel === null
                ? "public app"
                : storedDiscoveryChannel,
          },
        });
      } else {
        await apiClient.post("/webapp/private/rooms/:id/join", {
          param: { id: roomId },
          json: {
            discoveryChannel:
              storedDiscoveryChannel === null
                ? "public app"
                : storedDiscoveryChannel,
          },
        });
      }

      gaEventRoomJoin({
        roomUid: roomStats.id,
        userUid: currentUser.uid,
        utmSource,
        utmMedium,
        utmContent,
        origin: "oauth_callback",
        roomType: roomStats.isPublic ? "public" : "private",
        roomName: roomStats.translations.en.name,
        roomCount: currentUser.rooms.length,
        roomDiscoveryChannel:
          storedDiscoveryChannel === null
            ? "public app"
            : storedDiscoveryChannel,
      });
      // A signup invited to this room already completed at the end of its
      // onboarding (`oauthSignupCompletionStore`): one completion per signup.
      // Any other account here is a member joining the room.
      if (!consumeOAuthSignupCompleted(currentUser.uid)) {
        gaEventCompleteOnboarding(
          currentUser.uid,
          roomStats.id,
          "maybe_existing"
        );
      }

      await revalidate("/webapp/private/rooms");
      await reloadCurrentUser();

      clearRoomInviteCodeData();
      history.replace(getRoomInvitePathname(roomStats.id, playlistId));
      openRoomWelcome(
        roomStats.id,
        storedDiscoveryChannel === null ? "public app" : storedDiscoveryChannel
      );
    } catch {
      displayToast(t("Restricted access"), "error");
      clearRoomInviteCodeData();
      await errorRedirect();
    }
  };

  const redirectToRoom = (roomId: string) => {
    if (room === null) return;
    clearRoomInviteCodeData();
    history.replace(getRoomInvitePathname(roomId, playlistId));
  };

  // 1. Handle OAuth exchange
  useEffect(() => {
    if (oauthFlow.status !== "pending") return;

    const handleOAuthCallback = async () => {
      const oauthRoom = OauthRoomsConfig.find((r) => r.id === roomId);

      if (oauthRoom === undefined) {
        // Not an OAuth room, skip
        setOauthFlow({ status: "ready" });
        return;
      }

      try {
        const result = await oauthRoom.manager.handleCallback(
          globalThis.location.href
        );

        if (!result.success) {
          if (result.errorCode === "ALREADY_PROCESSING") {
            return;
          }

          // OAuth error → redirect to home
          console.error("OAuth callback failed:", result.error);
          displayToast(t("Something went wrong. Please try again."), "error");
          setOauthFlow({ status: "error" });
          await errorRedirect();
          return;
        }

        // OAuth success → mark as handled
        console.log("OAuth callback successful");
        setOauthFlow({ status: "ready" });
      } catch (error) {
        console.error("OAuth callback error:", error);
        displayToast(t("Something went wrong. Please try again."), "error");
        setOauthFlow({ status: "error" });
        await errorRedirect();
      }
    };

    void handleOAuthCallback();
  }, [roomId, oauthFlow.status, errorRedirect]);

  // 2. User and room checks
  useEffect(() => {
    if (oauthFlow.status !== "ready") return;
    if (!isLoadingUser && currentUser === null) {
      displayToast(t("Connect your account"), "info");
      history.replace("/");
    }

    if (roomQuery.state === "error") {
      const roomInviteCode =
        storedRoomInviteCodeData === null
          ? null
          : storedRoomInviteCodeData.roomInviteCode;
      console.error("Failed to get room data by invite code:", roomInviteCode);
      displayToast(t("Something went wrong. Please try again."), "error");
      void errorRedirect();
    }
  }, [
    oauthFlow.status,
    isLoadingUser,
    currentUser,
    isLoadingRoomData,
    errorRedirect,
  ]);

  // 3. Join room after OAuth
  useEffect(() => {
    // Membership refreshes during the join re-render this component. Only the
    // ready state may decide whether to join or redirect, so a confirmed join
    // cannot take the already-member branch before its consent check finishes.
    if (oauthFlow.status !== "ready") return;
    // Wait for user and room to be loaded
    if (isLoadingUser || isLoadingRoomData) return;
    if (currentUser === null || room === null) return;

    if (currentUser.rooms.includes(roomId)) {
      redirectToRoom(roomId);
      return;
    } else {
      setOauthFlow({ status: "joining" });
      void onJoinRoom();
    }
  }, [oauthFlow.status, isLoadingUser, isLoadingRoomData, currentUser, room]);
}
